import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal } from 'react-bootstrap';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { FiClock, FiCheck, FiAward, FiShield } from 'react-icons/fi';
import { authFetch } from '../utils/api';
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../styles/subscription.css';

// ─── Plan configuration (source of truth for the frontend display) ──────────
const PLAN_CONFIG = [
  /* Commented out for now
  {
    key: '1month',
    name: '1 Month',
    price: 499,
    duration: '30 Days',
    tag: null,
    icon: <FiZap size={28} />,
    color: '#6c63ff',
    description: 'Perfect for getting started with full premium access.',
    features: [
      'Unlimited invoices',
      'Unlimited customers',
      'Unlimited transactions',
      'Advanced reports & analytics',
      'Priority email support',
      'All premium features',
    ],
  },
  {
    key: '6month',
    name: '6 Months',
    price: 2499,
    duration: '180 Days',
    tag: 'MOST POPULAR',
    icon: <FiStar size={28} />,
    color: '#d63384',
    description: 'Great value for growing businesses. Save vs monthly!',
    features: [
      'Unlimited invoices',
      'Unlimited customers',
      'Unlimited transactions',
      'Advanced reports & analytics',
      'Priority email support',
      'All premium features',
      'Save ₹495 vs monthly!',
    ],
  },
  */
  {
    key: '1year',
    name: '1 Year',
    price: 3650,
    duration: '1 Year (365 Days)',
    tag: 'BEST VALUE',
    icon: <FiAward size={28} />,
    color: '#fd7e14',
    description: 'Best annual deal. Unlimited access at just ₹10 per day.',
    features: [
      'Unlimited invoices',
      'Unlimited customers',
      'Unlimited transactions',
      'All reports & analytics',
      'Priority support (phone & email)',
      'Early access to new features',
      'Maximum savings (₹10/day)!',
    ],
  },
  {
    key: '10year',
    name: '10 Years',
    price: 25000,
    duration: '10 Years',
    tag: 'LIFETIME',
    icon: <FiShield size={28} />,
    color: '#198754',
    description: 'One-time payment. 10 years of hassle-free billing. True lifetime value.',
    features: [
      '10 years of unlimited access',
      'One-time payment, no renewals',
      'All premium features included',
      'Dedicated priority support',
      'Early access to all new features',
      'Strictly Non-Refundable · Terms Apply',
    ],
  },
];

// ─── Individual plan card ────────────────────────────────────────────────────
const PlanCard = ({ config, dbPlan, isCurrentPlan, isAlmostExpired, isLoggedIn, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const navigate = useNavigate();

  const handleOpenTerms = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    if (!dbPlan) {
      setError('Plan not available. Please refresh.');
      return;
    }
    if (Number(config.price) === 0) {
      handlePay();
      return;
    }
    setShowTermsModal(true);
    setAgreeTerms(false);
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePay = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    if (!dbPlan) { setError('Plan not available. Please refresh.'); return; }

    setLoading(true);
    setError('');
    try {
      if (Number(config.price) === 0) {
        // Free plan - subscribe directly without Razorpay
        const res = await authFetch('/api/subscription/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: dbPlan.id }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || 'Failed to activate free trial');
        }
        const data = await res.json();
        
        window.dispatchEvent(new CustomEvent('paymentSuccess', { detail: data }));
        onPaymentSuccess && onPaymentSuccess(data);
        return;
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) throw new Error('Failed to load Razorpay SDK');

      // 1. Create order
      const orderRes = await authFetch('/api/payment/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: dbPlan.id }),
      });
      if (!orderRes.ok) throw new Error('Failed to create payment order');
      const orderData = await orderRes.json();
      if (!orderData.success) throw new Error('Failed to create payment order');

      const { order_id, amount, currency, key_id } = orderData;

      // 2. Open Razorpay checkout
      const options = {
        key: key_id,
        amount,
        currency,
        name: 'HamaraBillBook',
        description: `${config.name} Plan — ${config.duration}`,
        order_id,
        handler: async (response) => {
          try {
            // 3. Verify & activate
            const verifyRes = await authFetch('/api/payment/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
                plan_id: dbPlan.id,
              }),
            });
            if (!verifyRes.ok) throw new Error('Payment verification failed');
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              // Dispatch event so useTrialStatus refreshes immediately
              window.dispatchEvent(new CustomEvent('paymentSuccess', { detail: verifyData }));
              onPaymentSuccess && onPaymentSuccess(verifyData);

              Swal.fire({
                icon: 'success',
                title: 'Payment Successful!',
                html: `Your <b>${config.name}</b> subscription is now active.<br><br>Order ID: <b>${order_id}</b>`,
                showCancelButton: true,
                confirmButtonText: 'Download Receipt',
                cancelButtonText: 'Close',
                confirmButtonColor: '#28a745'
              }).then((result) => {
                if (result.isConfirmed) {
                  const doc = new jsPDF();
                  doc.setFontSize(22);
                  doc.text('HamaraBillBook', 14, 20);
                  doc.setFontSize(16);
                  doc.text('Subscription Receipt', 14, 30);
                  doc.setFontSize(11);
                  doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 40);
                  doc.text(`Order ID: ${order_id}`, 14, 46);
                  doc.text(`Payment ID: ${response.razorpay_payment_id}`, 14, 52);

                  autoTable(doc, {
                    startY: 60,
                    head: [['Description', 'Amount']],
                    body: [
                      [`${config.name} Subscription - ${config.duration}`, `Rs. ${amount / 100}`]
                    ],
                    theme: 'striped',
                  });

                  const finalY = (doc).lastAutoTable?.finalY || 100;
                  doc.setFontSize(10);
                  doc.text('Thank you for subscribing to HamaraBillBook.', 14, finalY + 20);
                  doc.save(`Receipt_${order_id}.pdf`);
                }
              });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (err) {
            Swal.fire('Error', 'Payment succeeded but verification failed. Please contact support.', 'error');
            setError('Payment succeeded but verification failed. Please contact support.');
          }
        },
        prefill: {
          name: localStorage.getItem('userName') || '',
          email: localStorage.getItem('userEmail') || '',
          contact: localStorage.getItem('userPhone') || '',
        },
        notes: { plan_id: dbPlan.id, plan_name: config.name },
        theme: { color: config.color },
        modal: { 
          ondismiss: () => {
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'info',
              title: 'Payment cancelled',
              showConfirmButton: false,
              timer: 3000
            });
            setLoading(false);
          } 
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (res) => {
        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          html: 'If any amount was deducted, it will be automatically refunded within <b>3-5 business days</b>.',
          confirmButtonText: 'Try Again'
        });
        setError(res.error?.description || 'Payment failed. Please try again.');
        setLoading(false);
      });
      razorpay.open();
    } catch (err) {
      setError(err.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const isPopular = config.tag === 'MOST POPULAR';
  const isLifetime = config.tag === 'LIFETIME';

  return (
    <div style={{
      border: isPopular ? `2px solid ${config.color}` : '1px solid #e9ecef',
      borderRadius: 20,
      padding: 28,
      background: '#fff',
      position: 'relative',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: isPopular ? `0 8px 32px ${config.color}22` : '0 2px 12px rgba(0,0,0,0.06)',
      transition: 'all 0.3s ease',
    }}>
      {/* Tag badge */}
      {config.tag && (
        <div style={{
          position: 'absolute',
          top: -14,
          left: '50%',
          transform: 'translateX(-50%)',
          background: config.color,
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          padding: '4px 16px',
          borderRadius: 20,
          letterSpacing: 1,
          whiteSpace: 'nowrap',
        }}>
          {config.tag}
        </div>
      )}

      {/* Icon + Name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: `${config.color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: config.color,
        }}>
          {config.icon}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>{config.name}</div>
          <div style={{ fontSize: 13, color: '#888' }}>{config.duration} access</div>
        </div>
      </div>

      {/* Price */}
      <div style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 38, fontWeight: 800, color: '#1a1a2e' }}>₹{config.price.toLocaleString('en-IN')}</span>
        {isLifetime
          ? <span style={{ fontSize: 14, color: '#888', marginLeft: 4 }}>one-time</span>
          : null}
      </div>

      {/* Non-Refundable Notice Badge */}
      <div style={{ marginBottom: 14 }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: '#fef2f2',
          color: '#991b1b',
          border: '1px solid #fecaca',
          borderRadius: 6,
          padding: '3px 8px',
          fontSize: 11.5,
          fontWeight: 600,
        }}>
          <span>🔒</span> Strictly Non-Refundable
        </span>
      </div>

      {/* Description */}
      <p style={{ fontSize: 13.5, color: '#666', marginBottom: 16, lineHeight: 1.5 }}>
        {config.description}
      </p>

      {/* Features */}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1 }}>
        {config.features.map((f, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, fontSize: 13.5, color: '#444' }}>
            <FiCheck size={14} style={{ color: config.color, flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </ul>

      {/* Error */}
      {error && <div style={{ color: '#dc3545', fontSize: 12, marginBottom: 10, textAlign: 'center' }}>{error}</div>}

      {/* CTA Button */}
      {!isLoggedIn ? (
        <button onClick={() => navigate('/login')} style={{
          width: '100%', padding: '13px 0', borderRadius: 10, border: `2px solid ${config.color}`,
          background: 'transparent', color: config.color, fontWeight: 700, fontSize: 15, cursor: 'pointer',
          transition: 'all 0.2s',
        }}>
          Login to Upgrade
        </button>
      ) : isCurrentPlan && !isAlmostExpired ? (
        <button disabled style={{
          width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
          background: '#e9ecef', color: '#999', fontWeight: 600, fontSize: 15, cursor: 'not-allowed',
        }}>
          Active Plan
        </button>
      ) : isCurrentPlan && isAlmostExpired ? (
        <button
          onClick={handleOpenTerms}
          disabled={loading}
          style={{
            width: '100%', padding: '13px 0', borderRadius: 10, border: `2px solid ${config.color}`,
            background: loading ? '#ccc' : '#fff', color: config.color, fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Processing…' : 'Renew Now'}
        </button>
      ) : (
        <button
          onClick={handleOpenTerms}
          disabled={loading}
          style={{
            width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
            background: loading ? '#ccc' : config.color,
            color: '#fff', fontWeight: 700, fontSize: 15,
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : `0 4px 16px ${config.color}44`,
            transition: 'all 0.2s',
          }}
        >
          {loading ? 'Processing…' : `Get ${config.name}`}
        </button>
      )}

      {/* Footer terms notice */}
      <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11.5, color: '#64748b' }}>
        Subject to{' '}
        <span
          style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
          onClick={handleOpenTerms}
        >
          Terms & No-Refund Policy
        </span>
      </div>

      {/* ── Terms & Conditions / Strict No-Refund Confirmation Modal ── */}
      <Modal 
        show={showTermsModal} 
        onHide={() => setShowTermsModal(false)} 
        centered 
        backdrop="static" 
        dialogClassName="subscription-terms-modal"
      >
        <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '16px 20px' }}>
          <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>📋</span> Subscription Terms & Non-Refundable Policy
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px', maxHeight: '72vh', overflowY: 'auto' }}>
          {/* Selected Plan Summary Banner */}
          <div style={{
            background: `${config.color}10`,
            border: `1.5px solid ${config.color}35`,
            borderRadius: 14,
            padding: '16px 18px',
            marginBottom: 18,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: config.color }}>
                Selected Subscription
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                {config.name} Plan
              </div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
                Duration: <strong>{config.duration} Access</strong> · Billed Once
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
                ₹{config.price.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 11.5, color: '#16a34a', fontWeight: 600 }}>
                Includes all applicable taxes
              </div>
            </div>
          </div>

          {/* Important Terms Box */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: '16px 18px',
            marginBottom: 18,
            fontSize: '13px',
            color: '#334155',
            lineHeight: 1.6
          }}>
            <h6 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px' }}>
              <span>⚠️</span> Mandatory Terms of Purchase & Refund Policy
            </h6>

            <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Immediate Digital Access:</strong> Upon payment verification, access to all premium features, unlimited billing, customer management, GST computation, cloud storage, and reports is provisioned instantly.
              </li>
              <li style={{ marginBottom: 8, color: '#b91c1c' }}>
                <strong>Strictly Non-Refundable (No Refunds):</strong> All subscription payments are final. <strong>Under no circumstances are full or partial refunds, chargebacks, pro-rata reimbursements, or cancellation refunds provided once a subscription is activated.</strong>
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Plan Duration:</strong> {config.name === '10 Years' ? 'The 10-Year Lifetime Plan provides 10 consecutive years (3,650 days) of uninterrupted premium service without any recurring renewal charges.' : `Your subscription remains active for exactly ${config.duration} from the timestamp of payment.`}
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Cancellation:</strong> You may request account cancellation anytime; however, early cancellation does not entitle the subscriber to any refund or financial credit for unused days.
              </li>
              <li>
                <strong>Agreement:</strong> By proceeding, you agree to abide by HamaraBillBook's <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Privacy Policy</a>.
              </li>
            </ul>
          </div>

          {/* Mandatory Checkbox Agreement */}
          <div style={{
            background: agreeTerms ? '#eff6ff' : '#f8fafc',
            border: agreeTerms ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
            borderRadius: 10,
            padding: '12px 14px',
            transition: 'all 0.2s ease'
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', margin: 0 }}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer', accentColor: '#2563eb', flexShrink: 0 }}
              />
              <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: 600, lineHeight: 1.5 }}>
                I have read and agree to the <strong>Terms of Service</strong> and explicitly acknowledge the <strong>Strict No-Refund Policy</strong> (No refunds after taking subscription).
              </span>
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderBottomLeftRadius: 18, borderBottomRightRadius: 18 }}>
          <Button variant="outline-secondary" onClick={() => setShowTermsModal(false)} style={{ borderRadius: 8, fontWeight: 600, padding: '8px 18px' }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!agreeTerms || loading}
            onClick={() => {
              setShowTermsModal(false);
              handlePay();
            }}
            style={{
              borderRadius: 8,
              fontWeight: 700,
              padding: '9px 20px',
              backgroundColor: agreeTerms ? config.color : '#94a3b8',
              borderColor: agreeTerms ? config.color : '#94a3b8',
              boxShadow: agreeTerms ? `0 4px 14px ${config.color}40` : 'none',
              cursor: agreeTerms ? 'pointer' : 'not-allowed'
            }}
          >
            {loading ? 'Processing...' : 'Accept & Proceed to Payment →'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ─── Main Subscription Page ──────────────────────────────────────────────────
const SubscriptionPage = () => {
  const navigate = useNavigate();
  const {
    currentSubscription,
    getCurrentPlan,
    refreshData,
  } = useSubscription();

  const [dbPlans, setDbPlans] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  // Cancellation feature commented out because subscriptions are strictly non-refundable (no refunds)
  // const [showCancelModal, setShowCancelModal] = useState(false);
  // const [cancellationReason, setCancellationReason] = useState('');
  // const [isCancelling, setIsCancelling] = useState(false);

  const currentPlan = getCurrentPlan();

  // Fetch DB plans to get IDs for payment in background
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await authFetch('/subscription/plans');
        if (!response.ok) throw new Error('Failed to fetch plans');
        const data = await response.json();
        setDbPlans(data.sort((a, b) => Number(a.price) - Number(b.price)));
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
    };
    fetchPlans();
    setIsLoggedIn(!!localStorage.getItem('token'));
  }, []);

  const handlePaymentSuccess = async (verifyData) => {
    setPaymentSuccess(verifyData);
    // Refresh subscription context so the UI updates immediately
    await refreshData();
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  /* Cancellation handler commented out because subscriptions are strictly non-refundable
  const handleCancelSubscription = async () => {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      await cancelSubscription(cancellationReason);
      setShowCancelModal(false);
      setCancellationReason('');
      Swal.fire({
        icon: 'success',
        title: 'Request Sent',
        text: 'Your cancellation request has been sent to our support team. We will contact you shortly.',
        confirmButtonColor: '#007bff',
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Cancellation Failed',
        text: err.message || 'An error occurred while cancelling your plan.',
      });
    } finally {
      setIsCancelling(false);
    }
  };
  */

  return (
    <section className="section py-5" id="subscription" style={{ background: '#f8f9ff', minHeight: '100vh' }}>
      <Container>

        {/* ── Success banner after payment ── */}
        {paymentSuccess && (
          <div style={{
            background: 'linear-gradient(135deg, #198754, #20c997)',
            color: '#fff', padding: '18px 28px', borderRadius: 16,
            marginBottom: 32, display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 4px 20px rgba(25,135,84,0.3)',
          }}>
            <FiCheck size={24} />
            <div>
              <strong>Payment Successful!</strong> Your subscription is now active. Redirecting…
            </div>
          </div>
        )}

        {/* ── Current subscription card ── */}
        {isLoggedIn && currentSubscription && (
          <Card className="border-0 shadow-sm mb-5" style={{ borderRadius: 20 }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h3 className="h5 mb-0 fw-bold">{currentPlan?.name || 'Free Trial'} Plan</h3>
                    <Badge bg={currentSubscription?.status === 'active' ? 'success' : currentSubscription?.status === 'trial' ? 'info' : 'warning'}>
                      <FiClock className="me-1" size={11} />
                      {(currentSubscription?.status || 'trial').toUpperCase()}
                    </Badge>
                  </div>
                  <p className="text-muted small mb-0">
                    {currentSubscription?.end_date
                      ? `Valid until ${new Date(currentSubscription.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`
                      : 'Active'}
                  </p>
                </div>

                {/* Cancel Plan button commented out because subscriptions are strictly non-refundable (no refunds) */}
                {/*
                {currentSubscription?.status !== 'cancelled' && currentSubscription?.status !== 'expired' && (
                  <div className="text-end">
                    {currentSubscription?.cancel_request_status === 'REJECTED' && (
                      <div className="text-danger small fw-bold mb-2">
                        Your request was denied by our management team.
                      </div>
                    )}
                    {currentSubscription?.cancel_request_status === 'PENDING' ? (
                      <Button variant="outline-secondary" size="sm" disabled>
                        Cancel Request Pending...
                      </Button>
                    ) : (
                      <Button variant="outline-danger" size="sm" onClick={() => setShowCancelModal(true)}>
                        Cancel Plan
                      </Button>
                    )}
                  </div>
                )}
                */}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* ── Heading ── */}
        <div className="text-center mb-5">
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#d63384', textTransform: 'uppercase' }}>
            PRICING
          </span>
          <h2 className="mt-2 mb-3 fw-bold" style={{ fontSize: 34, color: '#1a1a2e' }}>
            Simple, Transparent Pricing
          </h2>
          <p className="text-muted" style={{ maxWidth: 520, margin: '0 auto' }}>
            No hidden fees. Pick a plan that fits your timeline. Countdown starts right after purchase.
          </p>
        </div>

        {/* ── Plan Cards ── */}
        <Row className="g-4 justify-content-center">
          {PLAN_CONFIG.map((config) => {
            // Find matching DB plan by name (support 10 Years or legacy 25 Years)
            const dbPlan = dbPlans.find(p => p.name === config.name || (config.name === '10 Years' && (p.name === '10 Years' || p.name === '25 Years')));
            
            // It is the current plan ONLY if the status is not expired/cancelled and it matches the ID
            const isActiveSubscription = currentSubscription && 
                                         currentSubscription.status !== 'cancelled' && 
                                         currentSubscription.status !== 'expired';
            
            const isCurrentPlan = isLoggedIn && isActiveSubscription &&
              (currentSubscription.plan?.name === config.name ||
               (config.name === '10 Years' && (currentSubscription.plan?.name === '10 Years' || currentSubscription.plan?.name === '25 Years')) ||
               currentSubscription.plan?.id === dbPlan?.id ||
               currentSubscription.plan_id === dbPlan?.id);

            let isAlmostExpired = false;
            if (isCurrentPlan && currentSubscription.end_date) {
               const daysLeft = Math.ceil((new Date(currentSubscription.end_date) - new Date()) / (1000 * 60 * 60 * 24));
               isAlmostExpired = daysLeft <= 4;
            }

            return (
              <Col lg={5} md={6} sm={12} key={config.key} style={{ display: 'flex' }}>
                <PlanCard
                  config={config}
                  dbPlan={dbPlan}
                  isCurrentPlan={isCurrentPlan}
                  isAlmostExpired={isAlmostExpired}
                  isLoggedIn={isLoggedIn}
                  onPaymentSuccess={handlePaymentSuccess}
                />
              </Col>
            );
          })}
        </Row>

        {/* ── Note ── */}
        <p className="text-center text-muted small mt-4">
          🔒 Secure payments via Razorpay &nbsp;·&nbsp; All prices include GST &nbsp;·&nbsp; <strong style={{ color: '#dc3545' }}>Strictly Non-Refundable</strong> once activated &nbsp;·&nbsp; <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#d63384', textDecoration: 'underline', fontWeight: 600 }}>Terms &amp; Conditions Apply</a>
        </p>
      </Container>

      {/* ── Cancel Request Modal commented out because subscriptions are strictly non-refundable (no refunds) ── */}
      {/*
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered backdrop="static">
        <Modal.Header closeButton className="bg-danger text-white border-0" style={{ borderTopLeftRadius: 15, borderTopRightRadius: 15 }}>
          <Modal.Title className="fw-bold fs-5">Request Cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="lead mb-3 text-dark">Are you sure you want to cancel?</p>
          
          {(() => {
            let userObj = {};
            try { userObj = JSON.parse(localStorage.getItem('user')) || {}; } catch(e) {}
            return (
              <div className="mb-3">
                <div className="mb-2">
                  <label className="form-label small text-muted mb-1">Name</label>
                  <input type="text" className="form-control bg-light" value={userObj.name || 'User'} disabled />
                </div>
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1">Email</label>
                  <input type="email" className="form-control bg-light" value={userObj.email || 'Not provided'} disabled />
                </div>
              </div>
            );
          })()}

          <label className="form-label small text-muted mb-1">Reason for Cancellation</label>
          <textarea
            className="form-control"
            rows="3"
            placeholder="Tell us why (optional)"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 pe-4">
          <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>Keep Plan</Button>
          <Button variant="danger" disabled={isCancelling} onClick={handleCancelSubscription}>
            {isCancelling ? 'Submitting...' : 'Submit Request'}
          </Button>
        </Modal.Footer>
      </Modal>
      */}
    </section>
  );
};

export default SubscriptionPage;